import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CategoryIcon } from '../components/CategoryIcon';
import { Check } from '../components/Check';
import { DatePicker } from '../components/DatePicker';
import { Dialogue } from '../components/Dialogue';
import { EmptyState } from '../components/EmptyState';
import { MiniStat } from '../components/MiniStat';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { StreakDot } from '../components/StreakDot';
import { Triangle } from '../components/Triangle';
import { addDays, dayKey, prettyDate, prettyDay } from '../lib/date';
import { useStore } from '../lib/store';
import { currentStreak, hasCheckIn, hitRate, longestStreak, weekCount } from '../lib/streak';
import { FALLBACK_CAT } from '../lib/types';

export function DetailScreen() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { goals, categoryMap, checkInIndex, toggleToday, updateGoal, deleteGoal } = useStore();
  const [editing, setEditing] = useState(false);

  const goal = goals.find(g => g.id === id);
  if (!goal) {
    return (
      <EmptyState
        title="NO CHORE PICKED"
        body="That chore's gone from the garden, or was never planted. Pick another from your little book."
        cta={<><Triangle /> BROWSE CHORES</>}
        onCta={() => nav('/quests')}
      />
    );
  }

  const cat = categoryMap.get(goal.cat) ?? FALLBACK_CAT;
  const now = new Date();
  const today = dayKey(now);
  const done = goal.type === 'habit' ? hasCheckIn(checkInIndex, goal.id, today) : !!goal.completed_at;

  const streak = currentStreak(checkInIndex, goal.id, now);
  const longest = longestStreak(checkInIndex, goal.id);
  const week = weekCount(checkInIndex, goal.id, now);
  const rate = hitRate(checkInIndex, goal.id, 28, now);

  const history = useMemo(() => {
    const cells: { kind: 'hit' | 'miss' | 'future' }[] = [];
    for (let i = 27; i >= 0; i--) {
      const d = addDays(now, -i);
      if (d > now) cells.push({ kind: 'future' });
      else cells.push({ kind: hasCheckIn(checkInIndex, goal.id, dayKey(d)) ? 'hit' : 'miss' });
    }
    return cells;
  }, [checkInIndex, goal.id, now]);

  const handleDelete = async () => {
    if (!confirm(`Delete "${goal.title}"? This cannot be undone.`)) return;
    await deleteGoal(goal.id);
    nav('/quests');
  };

  return (
    <div className="grid-detail">
      <Panel title={`CHORE · ${goal.title.toUpperCase()}`} bodyClassName="panel-body">
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <div className="detail-cat-tile" style={{ background: cat.color }}>
            {cat.label[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pixel-font" style={{ fontSize: 14, color: '#1a110a' }}>
              {goal.type === 'habit' ? `${streak} DAY STREAK` : 'ONE-OFF ERRAND'}
            </div>
            <div
              style={{
                fontFamily: 'VT323',
                fontSize: 20,
                color: '#6b3a1d',
                marginTop: 6,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {goal.type === 'habit' ? (
                <>
                  <StreakDot streak={streak} />
                  Longest: {longest}d · This week: {week}/7
                </>
              ) : goal.completed_at ? (
                <>Completed {prettyDate(goal.completed_at)}</>
              ) : goal.due_date ? (
                <>Due {prettyDay(goal.due_date)}</>
              ) : (
                <>Pending</>
              )}
            </div>
          </div>
          <span className={`tag tag--${cat.tint}`}>
            <CategoryIcon slug={cat.icon} size={14} alt="" />
            {cat.label}
          </span>
        </div>

        {goal.type === 'habit' && (
          <>
            <div className="section-label">LAST 28 DAYS</div>
            <div className="history-grid">
              {history.map((h, i) => (
                <div
                  key={i}
                  className={`history-cell history-cell--${h.kind}`}
                  title={`Day ${i + 1}`}
                >
                  {h.kind === 'hit' ? '✓' : h.kind === 'miss' ? '·' : ''}
                </div>
              ))}
            </div>
          </>
        )}

        <Dialogue speaker="NOTEBOOK" style={{ marginTop: 20 }}>
          {goal.type === 'habit'
            ? streak >= longest && streak > 0
              ? `New record — ${streak} days, your longest bloom yet.`
              : longest > 0
              ? `${Math.max(longest - streak, 0)} more day${
                  longest - streak === 1 ? '' : 's'
                } matches your all-time best of ${longest}.`
              : `Check in today to start a little streak.`
            : goal.completed_at
            ? `All done. Rest a while, friend.`
            : `One small errand. Tick it off when it's tended.`}
        </Dialogue>

        <div className="detail-actions">
          <PixelButton onClick={() => nav('/quests')}><Triangle direction="left" /> BACK</PixelButton>
          <div className="detail-actions__right">
            <PixelButton onClick={() => setEditing(v => !v)}>{editing ? 'CLOSE' : 'EDIT'}</PixelButton>
            <PixelButton variant="ghost" onClick={handleDelete}>DELETE</PixelButton>
            <PixelButton variant="gold" onClick={() => toggleToday(goal)}>
              <Check /> {done ? 'UN-CHECK' : 'CHECK IN'}
            </PixelButton>
          </div>
        </div>

        {editing && <EditPanel goal={goal} onSave={patch => updateGoal(goal.id, patch)} />}
      </Panel>

      <div className="screens-col">
        <Panel title="CHORE STATS">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MiniStat label="CURRENT" value={`${streak}d`} icon="dot" streak={streak} />
            <MiniStat label="LONGEST" value={`${longest}d`} icon="star" />
            <MiniStat label="28-DAY" value={`${Math.round(rate * 100)}%`} />
            <MiniStat label="SINCE" value={prettyDate(goal.created_at)} />
          </div>
        </Panel>
        <Panel title="NOTES">
          <div className="notes-body">
            {goal.note || <em style={{ opacity: 0.6 }}>No notes yet.</em>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function EditPanel({
  goal,
  onSave,
}: {
  goal: ReturnType<typeof useStore>['goals'][number];
  onSave: (patch: Parameters<ReturnType<typeof useStore>['updateGoal']>[1]) => Promise<void>;
}) {
  const [title, setTitle] = useState(goal.title);
  const [note, setNote] = useState(goal.note);
  const [dueDate, setDueDate] = useState<string | null>(goal.due_date);
  const [pending, setPending] = useState(false);

  const save = async () => {
    setPending(true);
    try {
      await onSave({
        title: title.trim() || goal.title,
        note,
        due_date: goal.type === 'todo' ? dueDate : null,
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{ marginTop: 18, padding: 14, background: '#e8d29a', border: '3px solid #1a110a' }}>
      <div className="section-label">EDIT</div>
      <div style={{ marginBottom: 10 }}>
        <input className="pixel-input" value={title} onChange={e => setTitle(e.target.value)} />
      </div>
      {goal.type === 'todo' && (
        <div style={{ marginBottom: 10 }}>
          <div className="section-label" style={{ margin: '4px 0 8px' }}>DUE DATE</div>
          <DatePicker value={dueDate} onChange={setDueDate} />
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <textarea
          className="pixel-input"
          rows={3}
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PixelButton variant="gold" onClick={save} disabled={pending}>
          {pending ? 'SAVING…' : <><Triangle /> SAVE</>}
        </PixelButton>
      </div>
    </div>
  );
}
